import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getQontoStatus, QontoAuthError } from '@/services/qontoApi'
import { useQontoConnect } from '@/hooks/useQontoOAuth'
import { AlertTriangle, X, LogIn, Loader2 } from 'lucide-react'

/**
 * Global alert that detects an expired/invalid Qonto OAuth token
 * and prompts the admin to reconnect.
 *
 * Detection methods:
 * 1. `expires_at` from status endpoint is in the past
 * 2. The ['qonto', 'organization'] query (from QontoBalanceCard) has a QontoAuthError
 *
 * Place this once in AdminLayout so it covers all admin pages.
 */
export function QontoTokenExpiredAlert() {
  const [dismissed, setDismissed] = useState(false)
  const { connect, isLoading: isConnecting } = useQontoConnect()
  const queryClient = useQueryClient()

  const { data: status } = useQuery({
    queryKey: ['qonto', 'connection-status'],
    queryFn: getQontoStatus,
    staleTime: 5 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  // Read the org query error from cache (set by QontoBalanceCard) — no extra fetch
  const orgQueryState = queryClient.getQueryState(['qonto', 'organization'])
  const isAuthErrorFromApi = orgQueryState?.error instanceof QontoAuthError

  const isExpiredByDate = !!status?.connected && !!status?.expires_at && status.expires_at * 1000 < Date.now()
  const isTokenExpired = isExpiredByDate || isAuthErrorFromApi

  // Reset dismiss when connection state changes
  useEffect(() => {
    if (isTokenExpired) setDismissed(false)
  }, [isTokenExpired])

  if (!isTokenExpired || dismissed) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <button
          onClick={() => setDismissed(true)}
          className="absolute right-4 top-4 text-brown-400 hover:text-brown-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold-100">
          <AlertTriangle className="w-7 h-7 text-gold-600" />
        </div>

        <h3 className="text-center text-lg font-semibold text-brown-900">
          Token Qonto expire
        </h3>
        <p className="mt-2 text-center text-sm text-brown-600 leading-relaxed">
          Votre connexion a Qonto a expire. Les donnees bancaires (solde, transactions, factures) ne
          sont plus synchronisees. Reconnectez-vous pour restaurer l'integration.
        </p>

        <div className="mt-6 flex flex-col gap-3">
          <button
            onClick={() => connect()}
            disabled={isConnecting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-mint-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-mint-700 disabled:opacity-50"
          >
            {isConnecting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogIn className="w-4 h-4" />
            )}
            {isConnecting ? 'Redirection...' : 'Reconnecter Qonto'}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="text-sm text-brown-500 hover:text-brown-700 transition-colors"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  )
}
